// ─────────────────────────────────────────────────────────────────────────────
// KbConfirmationDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// Reusable confirmation dialog for Knowledge Base destructive actions.
// Follows the same pattern as InboxConfirmationDialogComponent.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';

import * as KbActions from '../../store/knowledge-base.actions';

export type KbConfirmationAction = 'delete';

export interface KbConfirmationDialogData {
  action: KbConfirmationAction;
  documentIds: string[];
}

@Component({
  selector: 'app-kb-confirmation-dialog',
  standalone: true,
  imports: [SharedModule, UiModule],
  templateUrl: './kb-confirmation-dialog.component.html',
  styleUrls: ['./kb-confirmation-dialog.component.scss'],
})
export class KbConfirmationDialogComponent {
  isLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<KbConfirmationDialogComponent>,
    private store: Store<AppState>,
    private actions$: Actions,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: KbConfirmationDialogData,
  ) {
    this.actions$.pipe(ofType(KbActions.DeleteDocumentsSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.isLoading = false;
      this.dialogRef.close(true);
    });

    this.actions$.pipe(ofType(KbActions.DeleteDocumentsError), takeUntil(this.destroy$)).subscribe(() => {
      this.isLoading = false;
    });
  }

  get icon(): string {
    switch (this.data.action) {
      case 'delete':
        return 'trash-cc';
    }
  }

  get title(): string {
    const count = this.data.documentIds.length;
    switch (this.data.action) {
      case 'delete':
        return count > 1
          ? this.translate.instant('knowledge_base.confirmation.delete.title_plural', { count })
          : this.translate.instant('knowledge_base.confirmation.delete.title');
    }
  }

  get description(): string {
    const count = this.data.documentIds.length;
    switch (this.data.action) {
      case 'delete':
        return count > 1
          ? this.translate.instant('knowledge_base.confirmation.delete.description_plural', { count })
          : this.translate.instant('knowledge_base.confirmation.delete.description');
    }
  }

  confirm(): void {
    if (this.isLoading) return;
    this.isLoading = true;

    switch (this.data.action) {
      case 'delete':
        this.store.dispatch(KbActions.DeleteDocuments({ documentIds: this.data.documentIds }));
        break;
    }
  }

  cancel(): void {
    if (this.isLoading) return;
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
